import { Component, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../core/services/auth/auth-service';
import { ApiService } from '../../core/services/api/api-service';
import { requests } from '../../core/config/config';
import { finalize } from 'rxjs';
import { Button } from '../../shared/components/button/button';
import { LayoutService } from '../../core/services/layout.service';
import { getAllowedRoutesForRole } from '../../core/config/menu-config';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, Button],
  templateUrl: './login.html',
  styleUrl: './login.scss',
})
export class Login {
  loginForm: FormGroup;
  isLoading = signal(false);
  errorMessage = signal<string | null>(null);

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private apiService: ApiService,
    private layoutService: LayoutService
  ) {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required]],
      password: ['', [Validators.required]]
    });
  }

  onSubmit(): void {
    if (!this.loginForm.valid) {
      this.loginForm.markAllAsTouched();
      this.errorMessage.set('Please fill in all required fields');
      return;
    }

    const { email, password } = this.loginForm.value;

    if (!email || !password) {
      this.errorMessage.set('Please fill in both email and password');
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set(null);

    // Call real API
    this.apiService
      .sendRequest(requests.login, 'post', { email, password })
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (res: any) => {
          const data = res?.data;
          if (data?.redirect_to_azure && data?.azure_url) {
            if (data?.temp_token) {
              this.authService.setAzureToken(data.temp_token);
            }
            window.location.href = data.azure_url;
            return;
          }

          if (data?.token) {
            // Set token and authentication state in auth service
            this.authService.setAuthToken(data.token);
            // Persist user data via auth service
            this.authService.setUser(data);

            // Set the first role as selected role in LayoutService
            const firstRole = data.roles?.[0]?.id ?? null;
            const firstRegion = data.regions?.[0]?.id ?? data.regions_with_countries?.[0]?.id ?? null;
            const firstRegionCountries = (data.regions_with_countries || [])
              .find((region: any) => String(region?.id) === String(firstRegion))
              ?.countries || data.countries || [];
            const firstCountry = firstRegionCountries.find((country: any) => country?.id != null)?.id ?? null;
            this.layoutService.setSelectedRole(firstRole);
            this.layoutService.setSelectedRegion(firstRegion)
            this.layoutService.setSelectedCountry(firstCountry)

            // Get allowed routes for this role and navigate to the first one
            const allowedRoutes = getAllowedRoutesForRole(firstRole);
            const targetRoute = allowedRoutes.length > 0 ? allowedRoutes[0] : '/dashboard';
            this.router.navigate([targetRoute]);
          } else {
            this.errorMessage.set(res?.message || 'Invalid response from server');
          }
        },
        error: (err: any) => {
          this.errorMessage.set(err?.error?.message || 'Login failed. Please try again.');
        }
      });
  }

}
